"use client";

import { ArrowRight, Menu } from "lucide-react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { useEffect, useState, type ReactNode } from "react";

import { LogoLockup } from "@/core/logo";
import { useSessionStatus } from "@/core/session";
import { SystemBannerBar } from "@/core/system-banner";
import { useSystemBanner } from "@/core/system-banner-context";
import { Drawer, cx } from "@/ds";
import { LanguagePicker, ThemePicker } from "@/shell/preferences";
import { ModelFinder } from "@/shell/top-bar-tools";

const LINKS = [
	{ key: "howItWorks", href: "/how-it-works" },
	{ key: "models", href: "/models" },
	{ key: "providers", href: "/providers" },
	{ key: "pricing", href: "/pricing" },
	{ key: "docs", href: "/docs" },
] as const;

/**
 * The public site's top bar: logo, main links, model finder, language and
 * theme, and sign-in or dashboard. Turns solid once the page scrolls.
 */
export function SiteHeader({
	sticky = true,
	children,
}: {
	sticky?: boolean;
	/** Extra controls shown before the account buttons. */
	children?: ReactNode;
}) {
	const t = useTranslations("site.nav");
	const posthog = usePostHog();
	const banner = useSystemBanner();
	const { isAuthenticated, isLoading } = useSessionStatus();
	const signedIn = isAuthenticated && !isLoading;
	const [scrolled, setScrolled] = useState(false);
	const [open, setOpen] = useState(false);

	useEffect(() => {
		const onScroll = () => setScrolled(window.scrollY > 8);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	const track = (link: string) =>
		posthog.capture("nav_link_clicked", { link, area: "navbar" });

	const account = signedIn ? (
		<Link
			href="/dashboard"
			className="inline-flex h-9 items-center gap-1.5 rounded-full bg-foreground px-4 text-sm font-medium text-background hover:opacity-90"
		>
			{t("dashboard")}
			<ArrowRight className="size-3.5" />
		</Link>
	) : (
		<>
			<Link
				href="/login"
				className="text-sm text-muted-foreground hover:text-foreground"
			>
				{t("login")}
			</Link>
			<Link
				href="/signup"
				onClick={() => track("signup")}
				className="inline-flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
			>
				{t("start")}
				<ArrowRight className="size-3.5" />
			</Link>
		</>
	);

	return (
		<>
			<SystemBannerBar banner={banner} />
			<header
				className={cx(
					"z-40 w-full transition-colors",
					sticky && "sticky top-0",
					scrolled || !sticky ? "bg-background" : "bg-transparent",
				)}
			>
				<div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
					<Link href="/" aria-label="Home" className="shrink-0">
						<LogoLockup className="h-6 w-auto text-foreground" />
					</Link>
					<nav aria-label="Main" className="hidden items-center gap-1 lg:flex">
						{LINKS.map((link) => (
							<Link
								key={link.href}
								href={link.href}
								onClick={() => track(link.key)}
								className="rounded-md px-3 py-1.5 text-[15px] text-muted-foreground hover:bg-muted hover:text-foreground"
							>
								{t(link.key)}
							</Link>
						))}
					</nav>
					<div className="ml-auto hidden items-center gap-2 lg:flex">
						<div className="w-48">
							<ModelFinder look="field" />
						</div>
						<LanguagePicker />
						<ThemePicker />
						{children}
						<div className="ml-2 flex items-center gap-3">{account}</div>
					</div>
					<button
						type="button"
						onClick={() => setOpen(true)}
						aria-label={t("openMenu")}
						className="ml-auto grid size-9 place-items-center rounded-md hover:bg-muted lg:hidden"
					>
						<Menu className="size-5" />
					</button>
				</div>
			</header>
			<Drawer open={open} onOpenChange={setOpen} label={t("openMenu")}>
				<div className="flex h-full flex-col gap-6 p-5">
					<LogoLockup className="h-6 w-auto text-foreground" />
					<ModelFinder look="field" shortcut={false} />
					<nav aria-label="Main" className="flex flex-col">
						{LINKS.map((link) => (
							<Link
								key={link.href}
								href={link.href}
								onClick={() => {
									track(link.key);
									setOpen(false);
								}}
								className="rounded-md px-2 py-3 text-lg hover:bg-muted"
							>
								{t(link.key)}
							</Link>
						))}
					</nav>
					<div className="flex items-center gap-2">
						<LanguagePicker />
						<ThemePicker />
					</div>
					{children}
					<div className="mt-auto flex items-center justify-between gap-3">
						{account}
					</div>
				</div>
			</Drawer>
		</>
	);
}
